export const VERSION_NUMBER_PATTERN =

  /^[0-9]+(?:\.[0-9]+){0,2}(?:[-+][A-Za-z0-9.-]+)?$/;



export const MAX_APK_BYTES = 150 * 1024 * 1024;

export const MAX_ICON_BYTES = 5 * 1024 * 1024;

export const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;



export const APK_INVALID_TYPE_MESSAGE = "Please upload an APK file.";

export const APK_TOO_LARGE_MESSAGE = "APK file must be 150 MB or smaller.";

export const APP_ICON_INVALID_TYPE_MESSAGE = "App icon must be a PNG or JPEG image.";

export const SCREENSHOT_INVALID_TYPE_MESSAGE = "Screenshots must be PNG or JPEG images.";



const PNG_JPEG_MIME_TYPES = new Set(["image/png", "image/jpeg", "image/jpg"]);



export type PublishFormValidationInput = {

  appName: string;

  categoryId: string;

  problemSolved: string;

  shortDescription: string;

  version: string;

  developerWebsite: string;

  publisherAgreement: boolean;

  apkFile: File | null;

  appIconFile: File | null;

  screenshot1File: File | null;

  screenshot2File: File | null;

  screenshot3File: File | null;

  hasExistingIcon?: boolean;

  existingScreenshotCount?: number;

};



export type VersionUpdateValidationInput = {

  newVersion: string;

  currentVersion: string | null;

  apkFile: File | null;

};



export type PublicationOutcome = {

  published: boolean;

  status: string;

};



export function validateVersionNumber(version: string): boolean {

  return VERSION_NUMBER_PATTERN.test(version.trim());

}



export function validateDeveloperWebsite(value: string): string[] {

  const trimmed = value.trim();

  if (!trimmed) {

    return [];

  }



  try {

    const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

    const url = new URL(normalized);

    if (!url.hostname) {

      return ["Developer Website must be a valid URL."];

    }

  } catch {

    return ["Developer Website must be a valid URL."];

  }



  return [];

}



const ALLOWED_APK_MIME_TYPES = new Set([
  "application/vnd.android.package-archive",
  "application/octet-stream",
  "application/zip",
  "",
]);

function isApkFile(file: File): boolean {
  return file.name.trim().toLowerCase().endsWith(".apk");
}

export function isAllowedApkMimeType(mimeType: string): boolean {
  return ALLOWED_APK_MIME_TYPES.has(mimeType.toLowerCase());
}

export function validateApkFileOnSelect(file: File): string | null {
  if (!isApkFile(file)) {
    return APK_INVALID_TYPE_MESSAGE;
  }

  if (file.size <= 0) {
    return "APK file is empty.";
  }

  if (file.size > MAX_APK_BYTES) {
    return APK_TOO_LARGE_MESSAGE;
  }

  return null;
}



function isPngOrJpegFile(file: File): boolean {

  const lowerName = file.name.toLowerCase();

  const mime = file.type.toLowerCase();

  const validExtension =

    lowerName.endsWith(".png") ||

    lowerName.endsWith(".jpg") ||

    lowerName.endsWith(".jpeg");

  const validMime = PNG_JPEG_MIME_TYPES.has(mime) || mime === "";

  return validExtension || validMime;

}



export function validateApkFileChoice(file: File | null): string | null {
  if (!file) {
    return "APK file is required.";
  }

  return validateApkFileOnSelect(file);
}



export function validateAppIconFileChoice(file: File | null): string | null {

  if (!file) {

    return "App icon is required.";

  }



  if (!isPngOrJpegFile(file)) {

    return APP_ICON_INVALID_TYPE_MESSAGE;

  }



  if (file.size <= 0) {

    return "App icon is empty.";

  }



  if (file.size > MAX_ICON_BYTES) {

    return "App icon exceeds the maximum allowed size.";

  }



  return null;

}



export function validateScreenshotFileChoice(file: File | null): string | null {

  if (!file) {

    return "Screenshot is required.";

  }



  if (!isPngOrJpegFile(file)) {

    return SCREENSHOT_INVALID_TYPE_MESSAGE;

  }



  if (file.size <= 0) {

    return "Screenshot is empty.";

  }



  if (file.size > MAX_SCREENSHOT_BYTES) {

    return "Screenshot exceeds the maximum allowed size.";

  }



  return null;

}



export function validateApkFile(file: File | null): string[] {

  const message = validateApkFileChoice(file);

  return message ? [message] : [];

}



export function validateAppIconFile(file: File | null, required: boolean): string[] {

  if (!file) {

    return required ? ["App icon is required."] : [];

  }



  const message = validateAppIconFileChoice(file);

  return message ? [message] : [];

}



export function validateScreenshotFile(file: File | null, required: boolean): string[] {

  if (!file) {

    return required ? ["Screenshot is required."] : [];

  }



  const message = validateScreenshotFileChoice(file);

  return message ? [message] : [];

}



export function collectCreatePublishValidationErrors(

  input: PublishFormValidationInput,

): string[] {

  const errors: string[] = [];



  if (!input.appName.trim()) errors.push("App name is required.");

  if (!input.categoryId.trim()) errors.push("Category is required.");

  if (!input.problemSolved.trim()) errors.push("Problem your app solves is required.");

  if (input.problemSolved.trim().length > 40) {

    errors.push("Problem your app solves must be 40 characters or fewer.");

  }

  if (!input.shortDescription.trim()) errors.push("Short description is required.");

  if (input.shortDescription.trim().length > 300) {

    errors.push("Short description must be 300 characters or fewer.");

  }

  if (!input.version.trim()) {

    errors.push("Version is required.");

  } else if (!validateVersionNumber(input.version)) {

    errors.push("Version must use a valid format (for example 1.0.0).");

  }



  errors.push(...validateDeveloperWebsite(input.developerWebsite));

  errors.push(...validateApkFile(input.apkFile));

  errors.push(...validateAppIconFile(input.appIconFile, true));

  errors.push(...validateScreenshotFile(input.screenshot1File, true));

  errors.push(...validateScreenshotFile(input.screenshot2File, true));

  errors.push(...validateScreenshotFile(input.screenshot3File, true));



  if (!input.publisherAgreement) {

    errors.push("Publisher Agreement acceptance is required.");

  }



  return errors;

}



export function collectEditPublishValidationErrors(

  input: PublishFormValidationInput,

): string[] {

  const errors: string[] = [];



  if (!input.appName.trim()) errors.push("App name is required.");

  if (!input.categoryId.trim()) errors.push("Category is required.");

  if (!input.problemSolved.trim()) errors.push("Problem your app solves is required.");

  if (input.problemSolved.trim().length > 40) {

    errors.push("Problem your app solves must be 40 characters or fewer.");

  }

  if (!input.shortDescription.trim()) errors.push("Short description is required.");

  if (input.shortDescription.trim().length > 300) {

    errors.push("Short description must be 300 characters or fewer.");

  }



  errors.push(...validateDeveloperWebsite(input.developerWebsite));



  if (!input.hasExistingIcon) {

    errors.push(...validateAppIconFile(input.appIconFile, true));

  } else if (input.appIconFile) {

    errors.push(...validateAppIconFile(input.appIconFile, false));

  }



  if ((input.existingScreenshotCount ?? 0) === 0) {

    errors.push(...validateScreenshotFile(input.screenshot1File, true));

    errors.push(...validateScreenshotFile(input.screenshot2File, true));

    errors.push(...validateScreenshotFile(input.screenshot3File, true));

  } else {

    if (input.screenshot1File) {

      errors.push(...validateScreenshotFile(input.screenshot1File, false));

    }

    if (input.screenshot2File) {

      errors.push(...validateScreenshotFile(input.screenshot2File, false));

    }

    if (input.screenshot3File) {

      errors.push(...validateScreenshotFile(input.screenshot3File, false));

    }

  }



  return errors;

}



export function collectVersionUpdateValidationErrors(

  input: VersionUpdateValidationInput,

): string[] {

  const errors: string[] = [];



  if (!input.newVersion.trim()) {

    errors.push("New version is required.");

  } else if (!validateVersionNumber(input.newVersion)) {

    errors.push("Version must use a valid format (for example 1.0.0).");

  } else if (input.currentVersion && input.newVersion.trim() === input.currentVersion) {

    errors.push("New version must be different from the current version.");

  }



  errors.push(...validateApkFile(input.apkFile));



  return errors;

}



export function resolvePostPublicationRoute(publication?: PublicationOutcome | null): string {

  if (!publication) {

    return "/publisher-dashboard";

  }



  if (publication.published || publication.status === "published") {

    return "/discover-apps";

  }



  if (publication.status === "security_blocked" || publication.status === "quarantined") {

    return "/publisher-dashboard";

  }



  return "/publisher-dashboard";

}



export function resolvePostPublicationMessage(

  publication?: PublicationOutcome | null,

): string | null {

  if (!publication) {

    return null;

  }



  if (publication.published || publication.status === "published") {

    return null;

  }



  if (publication.status === "pending_review") {

    return "Your app was submitted and is pending review.";

  }



  if (publication.status === "security_blocked" || publication.status === "quarantined") {

    return "Your app could not be published because the security scan blocked release.";

  }



  return "Your app submission is being processed.";

}


